import { useMemo, type Dispatch, type SetStateAction } from 'react'
import { toast } from 'sonner'
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts'
import { detectShortcutOs, focusAppPanel } from '@/lib/keyboard-platform'
import type { OpenTab } from '../features/document-tabs'
import { isSessionScratchPath } from '@/lib/session-scratch-path'
import { testIds } from '@/lib/test-ids'

/** #panel-collapse owns Cmd/Ctrl+B and +J while that gallery scene is mounted. */
function panelCollapseSceneOwnsShortcuts(): boolean {
  if (typeof document === 'undefined') return false
  return Boolean(
    document.querySelector(`[data-testid="${testIds.maaterUiGallery.panelCollapseScene}"]`),
  )
}

export type ResizableLayoutKeyboardShortcutDeps = {
  handleGoBack: () => void
  handleGoForward: () => void
  switchToPreviousTab: () => void
  switchToNextTab: () => void
  activeTabId: string | null
  handleDocumentClose: (documentId: string) => void
  handleReopenLastClosedTab: () => Promise<void>
  openTabs: OpenTab[]
  setOpenTabs: Dispatch<SetStateAction<OpenTab[]>>
  sidebarCollapsed: boolean
  toggleSidebarCollapsed: () => void
  /** Persists chat width then toggles agent strip (must match header + ChatPane). */
  toggleAgentCollapsed: () => void
  setCommandPaletteOpen: (value: boolean | ((prev: boolean) => boolean)) => void
  setKeyboardShortcutsOpen: (value: boolean | ((prev: boolean) => boolean)) => void
}

/**
 * Single registration for all layout-level shortcuts (tab strip + chrome toggles + palette).
 */
export function useResizableLayoutKeyboardShortcuts(
  d: ResizableLayoutKeyboardShortcutDeps
) {
  const shortcutOs = useMemo(() => detectShortcutOs(), [])

  const shortcuts = useMemo(() => {
    const closeActiveTab = (e?: KeyboardEvent) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
      }
      if (d.activeTabId) {
        d.handleDocumentClose(d.activeTabId)
      }
    }

    const openCommandPalette = () => d.setCommandPaletteOpen(true)

    const pinActiveTab = () => {
      const currentTab = d.openTabs.find((tab) => tab.id === d.activeTabId)
      if (d.activeTabId && currentTab?.type !== 'explorer' && currentTab) {
        d.setOpenTabs(
          d.openTabs.map((tab) =>
            tab.id === d.activeTabId ? { ...tab, isPinned: !tab.isPinned } : tab
          )
        )
      }
    }

    const paletteShortcuts =
      shortcutOs === 'mac'
        ? [
            {
              key: 'p',
              modKey: true,
              shiftKey: false,
              handler: openCommandPalette,
              description: 'Open command palette',
            },
            {
              key: 'k',
              modKey: true,
              shiftKey: false,
              handler: () => {
                const hintKey = 'cmdKHintShown'
                if (!localStorage.getItem(hintKey)) {
                  localStorage.setItem(hintKey, '1')
                  toast.info('Cmd+K is now Cmd+P', {
                    description: 'The command palette shortcut has moved to Cmd+P.',
                    duration: 4000,
                  })
                }
                openCommandPalette()
              },
              description: 'Open command palette (legacy shortcut)',
            },
            {
              key: 'p',
              modKey: true,
              shiftKey: true,
              handler: pinActiveTab,
              description: 'Pin/unpin current tab',
            },
          ]
        : [
            {
              key: 'p',
              modKey: true,
              shiftKey: true,
              handler: openCommandPalette,
              description: 'Open command palette',
            },
            {
              key: 'k',
              modKey: true,
              shiftKey: true,
              handler: openCommandPalette,
              description: 'Open command palette (legacy shortcut)',
            },
            {
              key: 'p',
              modKey: true,
              altKey: true,
              shiftKey: false,
              handler: pinActiveTab,
              description: 'Pin/unpin current tab',
            },
          ]

    return [
      {
        key: 'n',
        modKey: true,
        handler: () => {
          const tab = d.openTabs.find((t) => t.id === d.activeTabId)
          const isDocTab = tab && !tab.id.startsWith('chat-conversation-') && tab.type !== 'explorer'
          const inheritOpenDoc = isDocTab && !isSessionScratchPath(tab.filePath)
          window.dispatchEvent(
            new CustomEvent('chat-create-new', {
              detail: inheritOpenDoc
                ? { contextDocumentId: tab.id, contextDocumentTitle: tab.title }
                : {},
            }),
          )
        },
        description: 'New chat',
      },
      {
        key: 'ArrowLeft',
        modKey: true,
        altKey: true,
        handler: () => d.handleGoBack(),
        description: 'Go back within tab',
      },
      {
        key: 'ArrowRight',
        modKey: true,
        altKey: true,
        handler: () => d.handleGoForward(),
        description: 'Go forward within tab',
      },
      {
        key: '[',
        modKey: true,
        shiftKey: true,
        handler: () => d.switchToPreviousTab(),
        description: 'Switch to previous tab',
      },
      {
        key: ']',
        modKey: true,
        shiftKey: true,
        handler: () => d.switchToNextTab(),
        description: 'Switch to next tab',
      },
      {
        key: 'w',
        modKey: true,
        altKey: true,
        shiftKey: false,
        handler: closeActiveTab,
        description: 'Close active tab (recommended in Chrome)',
      },
      {
        key: 'w',
        modKey: true,
        shiftKey: false,
        altKey: false,
        handler: closeActiveTab,
        description: 'Close active tab (when the browser does not take mod+W)',
      },
      {
        key: 't',
        modKey: true,
        shiftKey: true,
        handler: (e?: KeyboardEvent) => {
          if (e) {
            e.preventDefault()
            e.stopPropagation()
          }
          void d.handleReopenLastClosedTab()
        },
        description: 'Reopen last closed tab',
      },
      {
        key: 'b',
        modKey: true,
        handler: () => {
          if (panelCollapseSceneOwnsShortcuts()) return false
          d.toggleSidebarCollapsed()
        },
        description: 'Toggle sidebar',
      },
      {
        key: 'j',
        modKey: true,
        handler: () => {
          if (panelCollapseSceneOwnsShortcuts()) return false
          d.toggleAgentCollapsed()
        },
        description: 'Toggle right sidebar',
      },
      ...paletteShortcuts,
      {
        key: 'f',
        modKey: true,
        shiftKey: true,
        handler: () => {
          window.dispatchEvent(
            new CustomEvent('flownotes:open-search-view', { detail: { query: '' } })
          )
        },
        description: 'Open search view',
      },
      {
        key: '?',
        modKey: true,
        shiftKey: true,
        handler: () => d.setKeyboardShortcutsOpen(true),
        description: 'Show keyboard shortcuts',
      },
      {
        key: '1',
        modKey: true,
        handler: () => focusAppPanel('sidebar'),
        description: 'Focus sidebar',
      },
      {
        key: '2',
        modKey: true,
        handler: () => focusAppPanel('center'),
        description: 'Focus center panel',
      },
      {
        key: '3',
        modKey: true,
        handler: () => focusAppPanel('agent'),
        description: 'Focus agent pane',
      },
    ]
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `d` is an inline object from parent; listing fields matches prior behavior
    [
      d.handleGoBack,
      d.handleGoForward,
      d.switchToPreviousTab,
      d.switchToNextTab,
      d.activeTabId,
      d.handleDocumentClose,
      d.handleReopenLastClosedTab,
      d.openTabs,
      d.setOpenTabs,
      d.toggleSidebarCollapsed,
      d.sidebarCollapsed,
      d.toggleAgentCollapsed,
      d.setCommandPaletteOpen,
      d.setKeyboardShortcutsOpen,
      shortcutOs,
    ]
  )

  useKeyboardShortcuts(shortcuts)
}
